import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import {
  getAllAuthors,
  getAuthorWithArtworks,
} from "../../../lib/catalog/authors"
import { BAD_REQUEST } from "../../constants/errors"
import { createError } from "../../utils"
import { Text, Title } from "../../components/typography"
import { useTranslation } from "react-i18next"
import { ErrorView } from "../../components/feedback"
import {
  DEFAULT_USER_AVATAR,
  DEFAULT_WALLPAPER_URL,
  STATIC_PAGE_REVALIDATE_INTERVAL,
} from "../../constants/application-settings"
import React from "react"
import _ from "lodash"
import { SocialBadge } from "../../components/social-badge"
import { getSocialIcon } from "../../components/social-badge/social-icons"
import { ArtworkGallery } from "../../components/artwork"
import { Seo } from "../../components/seo"
import { GlobeAltIcon, MailIcon } from "@heroicons/react/outline"

export async function getStaticPaths() {
  const allAuthors = await getAllAuthors()
  const allPaths = allAuthors.map(author => {
    return { params: { userId: Buffer.from(author.userId).toString("base64") } }
  })

  return {
    paths: allPaths,
    fallback: true, // false or 'blocking'
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params?.userId) {
    return {
      props: {
        error: createError(BAD_REQUEST, 400, "userId is required"),
      },
    }
  }

  const encodedUserId = Array.isArray(params.userId)
    ? params.userId[0]
    : params.userId

  const userId = Buffer.from(encodedUserId, "base64").toString()

  const author = await getAuthorWithArtworks(userId)

  return { props: { author }, revalidate: STATIC_PAGE_REVALIDATE_INTERVAL }
}

const AuthorPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  author,
}) => {
  const { t } = useTranslation()

  if (!author) {
    return <ErrorView error={new Error(t("error:authorNotFound"))} />
  }

  const {
    firstname,
    lastname,
    pictureId,
    wallpaperId,
    title,
    email,
    website,
    biography,
    facebook,
    twitter,
    youtube,
    linkedin,
    tiktok,
    showEmail,
    artworks,
  } = author

  const socialLinks = {
    facebook,
    twitter,
    youtube,
    linkedin,
    tiktok,
  }

  const authorDisplayName =
    firstname && lastname ? `${firstname} ${lastname}` : t("label:anonymous")

  return (
    <div>
      <Seo title={authorDisplayName} description={biography} />
      <div className="h-[150px] overflow-hidden">
        <img
          src={
            wallpaperId
              ? `/api/v1/previews/${wallpaperId}`
              : DEFAULT_WALLPAPER_URL
          }
          alt="wallpaper"
        />
      </div>
      <div className="container mx-auto">
        <div className="flex space-x-4">
          <div className="flex-none shrink min-w-[300px] max-w-[300px] bg-arezzo-light mt-[-50px] rounded p-4 mb-4">
            <div className="text-center space-y-4 pb-8">
              <div className="w-[100px] h-[100px] rounded-full border border-2 border-arezzo overflow-hidden m-auto">
                <img
                  src={
                    pictureId
                      ? `/api/v1/previews/${wallpaperId}`
                      : DEFAULT_USER_AVATAR
                  }
                  alt="avatar"
                />
              </div>
              <Title>{authorDisplayName}</Title>
              <Text type="secondary">{title || "--"}</Text>
            </div>
            <div className="space-y-2 pb-8">
              <Title level={3}>{t("label:information")}</Title>
              {showEmail && email && (
                <div className="flex space-x-2">
                  <MailIcon className="h-6 text-arezzo" />
                  <a className="text-arezzo" href={`mailto:${email}`}>
                    {email}
                  </a>
                </div>
              )}
              {website && (
                <div className="flex space-x-2">
                  <GlobeAltIcon className="h-6 text-arezzo" />
                  <a
                    className="text-arezzo"
                    href={website}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("label:personalWebSite")}
                  </a>
                </div>
              )}
              <div className="flex space-x-2">
                {_.map(socialLinks, (link, key) => {
                  return link ? (
                    <SocialBadge
                      key={key}
                      icon={getSocialIcon(key)}
                      url={link}
                    />
                  ) : null
                })}
              </div>
            </div>
            <div>
              <Title level={3}>{t("label:biography")}</Title>
              <p>{biography}</p>
            </div>
          </div>
          <div className="grow">
            <ArtworkGallery data={artworks} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthorPage
